import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import type { SvgIconComponent } from '@mui/icons-material';
import BoltIcon from '@mui/icons-material/Bolt';
import GroupsIcon from '@mui/icons-material/Groups';
import TollIcon from '@mui/icons-material/Toll';
import type { ComponentType } from 'react';
import { MPointsPanel, PointsPanel, ReferralsPanel } from './panels.tsx';

/**
 * v1 ships the Rewards menu as plain navigation. The data panels (summary + per-item stats) are
 * built but gated off until backend ships `GET /v1/profile/header-summary`; v2 flips this back on
 * and rewires the panels to it. A hardcoded constant on purpose: flipped in code, not remotely.
 *
 * While `false` nothing in `panels.tsx` mounts, so the header never calls `/profile/mpoints` or
 * `/profile/rewards`. `/profile/points` (points pill) and `/profile/referrals` (`useIsPartner`)
 * keep their own callers.
 *
 * Proposed contract (X-Auth-Nonce, 404 = empty profile, 0 = unranked, polled at 60s):
 *   { at,
 *     points:      { total, rank, last_epoch, origins_badge } | null,
 *     mpoints:     { total, settled: { e, mp, r } | null } | null,
 *     referrals:   { total_rewards, total_payouts, active } | null,
 *     leaderboard: { month_rank, month_volume, all_time_rank } }
 *
 * Typed `boolean` so the disabled branches still typecheck.
 */
export const REWARDS_MENU_DETAILS_ENABLED: boolean = false;

export interface RewardsMenuItem {
  key: string;
  /** Route the item navigates to; also drives active-state detection. */
  path: '/mpoints' | '/points' | '/referrals';
  feature: 'rewardsEnabled' | 'pointsEnabled' | 'referralDashboardEnabled';
  label: MessageDescriptor;
  description: MessageDescriptor;
  Icon: SvgIconComponent;
  /** Data panel shown while the item is hovered (v2, see REWARDS_MENU_DETAILS_ENABLED). */
  Panel: ComponentType;
}

export const rewardsMenuItems: readonly RewardsMenuItem[] = [
  {
    key: 'points',
    path: '/points',
    feature: 'pointsEnabled',
    label: msg({
      comment:
        'Rewards menu Points item label. "Points" is the name of a reward currency — keep it in Latin script and do not translate or transliterate it.',
      message: 'Points',
    }),
    description: msg({ comment: 'Rewards menu Points item description', message: 'Perpl points' }),
    Icon: TollIcon,
    Panel: PointsPanel,
  },
  {
    key: 'mpoints',
    path: '/mpoints',
    feature: 'rewardsEnabled',
    label: msg({ comment: 'Rewards menu mPoints item label', message: 'mPoints' }),
    description: msg({ comment: 'Rewards menu mPoints item description', message: 'Seasonal Monad trading rewards' }),
    Icon: BoltIcon,
    Panel: MPointsPanel,
  },
  {
    key: 'referrals',
    path: '/referrals',
    feature: 'referralDashboardEnabled',
    label: msg({ comment: 'Rewards menu Referrals item label', message: 'Referrals' }),
    description: msg({ comment: 'Rewards menu Referrals item description', message: 'Invite traders, earn fees' }),
    Icon: GroupsIcon,
    Panel: ReferralsPanel,
  },
] as const;

/** True when the current pathname belongs to any rewards sub-page. */
export const isRewardsPath = (pathname: string): boolean =>
  rewardsMenuItems.some((item) => pathname === item.path || pathname.startsWith(`${item.path}/`));
